<template>
  <Transition name="fade">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[9998] bg-black/30 backdrop-blur-[1px]"
      @click.self="closeDrawer"
    />
  </Transition>

  <Transition name="slide">
    <aside
      v-if="modelValue"
      class="fixed right-0 top-0 z-[9999] h-screen w-full max-w-2xl overflow-y-auto bg-white shadow-2xl"
    >
      <div
        class="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-6 py-5 backdrop-blur"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <p
              class="text-xs font-semibold uppercase tracking-[0.2em] text-[#2f8f2f]"
            >
              Cliente visitado
            </p>

            <h2 class="mt-1 text-2xl font-bold text-gray-900">
              {{ isEdit ? "Editar empresa" : "Nova empresa" }}
            </h2>

            <p class="mt-1 text-sm text-gray-500">
              Esses dados serão usados automaticamente na geração dos relatórios
              técnicos.
            </p>
          </div>

          <button
            type="button"
            class="rounded-xl p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            @click="closeDrawer"
          >
            <PhX class="h-5 w-5" />
          </button>
        </div>
      </div>

      <form class="px-6 py-6" @submit.prevent="submitForm">
        <div class="mb-8 rounded-2xl border border-gray-100 bg-gray-50/60 p-5">
          <div class="flex flex-col gap-5 sm:flex-row sm:items-center">
            <div
              class="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
            >
              <img
                v-if="logoPreview"
                :src="logoPreview"
                alt="Prévia da logo"
                class="h-full w-full object-contain p-2"
              />

              <PhBuildings v-else class="h-10 w-10 text-gray-300" />
            </div>

            <div class="flex-1">
              <h3 class="text-sm font-semibold text-gray-800">
                Logo da empresa
              </h3>

              <p class="mt-1 text-sm text-gray-500">
                A logo será usada na capa e nos layouts personalizados do
                relatório.
              </p>

              <label
                class="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-xl border border-green-100 bg-white px-4 py-2.5 text-sm font-semibold text-[#2f8f2f] shadow-sm transition hover:bg-green-50"
              >
                <PhUploadSimple class="h-5 w-5" />
                Selecionar logo

                <input
                  type="file"
                  class="hidden"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  @change="handleLogoChange"
                />
              </label>

              <button
                v-if="logoPreview"
                type="button"
                class="ml-2 inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                @click="removeLogo"
              >
                Remover
              </button>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Razão social <span class="text-red-500">*</span>
            </label>

            <input
              v-model="form.corporate_name"
              type="text"
              class="report-input"
              placeholder="Ex: Art Fritas Indústria e Comércio LTDA"
              required
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Nome fantasia
            </label>

            <input
              v-model="form.trade_name"
              type="text"
              class="report-input"
              placeholder="Ex: Art Fritas"
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              CNPJ
            </label>

            <input
              v-model="form.cnpj"
              type="text"
              class="report-input"
              placeholder="00.000.000/0001-00"
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              E-mail
            </label>

            <input
              v-model="form.email"
              type="email"
              class="report-input"
              placeholder="contato@empresa.com.br"
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Telefone
            </label>

            <input
              v-model="form.phone"
              type="text"
              class="report-input"
              placeholder="(15) 99999-9999"
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Responsável
            </label>

            <input
              v-model="form.responsible_name"
              type="text"
              class="report-input"
              placeholder="Nome do responsável"
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Cargo do responsável
            </label>

            <input
              v-model="form.responsible_role"
              type="text"
              class="report-input"
              placeholder="Ex: Gerente, Técnico, RH..."
            />
          </div>
        </div>

        <div class="mt-8">
          <h3 class="mb-4 text-base font-bold text-gray-800">Endereço</h3>

          <div class="grid grid-cols-1 gap-5 md:grid-cols-6">
            <div class="md:col-span-2">
              <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                CEP
              </label>

              <input
                v-model="form.zipcode"
                type="text"
                class="report-input"
                placeholder="00000-000"
              />
            </div>

            <div class="md:col-span-4">
              <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                Rua / Endereço
              </label>

              <input
                v-model="form.street"
                type="text"
                class="report-input"
                placeholder="Rua, avenida, estrada..."
              />
            </div>

            <div class="md:col-span-2">
              <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                Número
              </label>

              <input
                v-model="form.number"
                type="text"
                class="report-input"
                placeholder="Nº"
              />
            </div>

            <div class="md:col-span-4">
              <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                Complemento
              </label>

              <input
                v-model="form.complement"
                type="text"
                class="report-input"
                placeholder="Bloco, sala, referência..."
              />
            </div>

            <div class="md:col-span-2">
              <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                Bairro
              </label>

              <input
                v-model="form.district"
                type="text"
                class="report-input"
                placeholder="Bairro"
              />
            </div>

            <div class="md:col-span-3">
              <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                Cidade
              </label>

              <input
                v-model="form.city"
                type="text"
                class="report-input"
                placeholder="Cidade"
              />
            </div>

            <div class="md:col-span-1">
              <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                UF
              </label>

              <input
                v-model="form.state"
                type="text"
                maxlength="2"
                class="report-input uppercase"
                placeholder="SP"
              />
            </div>
          </div>
        </div>

        <div class="mt-8">
          <label class="mb-1.5 block text-sm font-semibold text-gray-700">
            Observações
          </label>

          <textarea
            v-model="form.notes"
            rows="4"
            class="report-input resize-none"
            placeholder="Informações internas sobre essa empresa..."
          />
        </div>

        <div
          class="sticky bottom-0 mt-8 flex items-center justify-end gap-3 border-t border-gray-100 bg-white py-5"
        >
          <button
            type="button"
            class="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
            @click="closeDrawer"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#2f8f2f] to-[#5dbb45] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="loading"
          >
            <PhCircleNotch v-if="loading" class="h-5 w-5 animate-spin" />
            <PhCheck v-else class="h-5 w-5" />

            {{ isEdit ? "Salvar alterações" : "Cadastrar empresa" }}
          </button>
        </div>
      </form>
    </aside>
  </Transition>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import {
  PhBuildings,
  PhCheck,
  PhCircleNotch,
  PhUploadSimple,
  PhX,
} from "@phosphor-icons/vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  client: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "submit", "close"]);

const selectedLogoFile = ref(null);
const logoPreview = ref("");

const form = reactive(getDefaultForm());

const isEdit = computed(() => Boolean(form.id));

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return;

    fillForm(props.client);
  }
);

watch(
  () => props.client,
  (client) => {
    if (props.modelValue) {
      fillForm(client);
    }
  }
);

function getDefaultForm() {
  return {
    id: null,
    corporate_name: "",
    trade_name: "",
    cnpj: "",
    email: "",
    phone: "",
    responsible_name: "",
    responsible_role: "",
    zipcode: "",
    street: "",
    number: "",
    complement: "",
    district: "",
    city: "",
    state: "",
    logo_path: "",
    logo_url: "",
    notes: "",
    is_active: true,
  };
}

function fillForm(client = null) {
  Object.assign(form, getDefaultForm());

  selectedLogoFile.value = null;
  logoPreview.value = "";

  if (!client) return;

  Object.assign(form, {
    ...getDefaultForm(),
    ...client,
  });

  logoPreview.value = client.logo_url || "";
}

function handleLogoChange(event) {
  const [file] = event.target.files || [];

  if (!file) return;

  selectedLogoFile.value = file;

  if (logoPreview.value && logoPreview.value.startsWith("blob:")) {
    URL.revokeObjectURL(logoPreview.value);
  }

  logoPreview.value = URL.createObjectURL(file);
}

function removeLogo() {
  if (logoPreview.value && logoPreview.value.startsWith("blob:")) {
    URL.revokeObjectURL(logoPreview.value);
  }

  selectedLogoFile.value = null;
  logoPreview.value = "";
  form.logo_path = "";
  form.logo_url = "";
}

function submitForm() {
  const payload = {
    id: form.id,
    corporate_name: form.corporate_name?.trim(),
    trade_name: form.trade_name?.trim(),
    cnpj: form.cnpj?.trim(),
    email: form.email?.trim(),
    phone: form.phone?.trim(),
    responsible_name: form.responsible_name?.trim(),
    responsible_role: form.responsible_role?.trim(),
    zipcode: form.zipcode?.trim(),
    street: form.street?.trim(),
    number: form.number?.trim(),
    complement: form.complement?.trim(),
    district: form.district?.trim(),
    city: form.city?.trim(),
    state: form.state?.trim()?.toUpperCase(),
    logo_path: form.logo_path,
    logo_url: form.logo_url,
    notes: form.notes?.trim(),
    is_active: form.is_active ?? true,
  };

  emit("submit", {
    payload,
    logoFile: selectedLogoFile.value,
    logoPreview: logoPreview.value,
    isEdit: isEdit.value,
  });
}

function closeDrawer() {
  emit("update:modelValue", false);
  emit("close");
}
</script>

<style scoped>
.report-input {
  @apply w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-700 shadow-sm outline-none transition placeholder:text-gray-300 focus:border-[#2f8f2f] focus:ring-2 focus:ring-green-100;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
